import {Icon} from '@iconify/react';
import {useQuery} from '@tanstack/react-query';
import {motion} from 'framer-motion';
import {ReactNode, useCallback, useEffect, useMemo, useState} from 'react';
import services from '../../services';
import {TournamentRoundupLeg} from '../../types';

/**
 * "Road to victory" — the end-of-event celebration on the live view.
 *
 * Two beats: the champion is announced centre-screen with confetti, then the matches that got them
 * there play through as a carousel (auto-advancing so it runs hands-free on a venue screen, but
 * swipeable/clickable the moment anyone takes control).
 *
 * The route is computed server-side (TournamentRoundupService) rather than from the bracket already
 * on the client, so this and the organizer's PDF report are guaranteed to tell the same story, and
 * the walk isn't implemented twice in two languages.
 *
 * Works for every draw type: knockouts label their legs Final/Semi-final/Quarter-final, round-robin
 * and Swiss fall back to round numbers with the winner taken from the standings.
 *
 * Its pieces — the confetti, the champion's reveal, the route as a carousel or a list — are
 * exported: the "Share the results" screen and the public results page are built from the same
 * parts, so the three never drift apart.
 */

/** How long the winner reveal holds before the route starts. */
const REVEAL_MS = 3200;
/** Dwell time per match in the carousel. */
const SLIDE_MS = 4000;

const CONFETTI_COLORS = ['#f5c518', '#082F8A', '#3b6bdc', '#ffffff', '#16a34a', '#e0447c'];

/** Remembers that this browser has already seen a given event's celebration. */
const seenKey = (uid: string) => `sp_rtv_seen_${uid}`;

function prefersReducedMotion(): boolean {
    try {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
        return false;
    }
}

/**
 * Falling confetti for the reveal. Purely decorative — skipped when reduced motion is requested.
 * `once` is a single burst (the completion screen); otherwise it falls for as long as it is shown.
 */
export function Confetti({count = 70, once = false}: {count?: number; once?: boolean}) {
    // Generated once per mount so pieces keep their identity across re-renders.
    const pieces = useMemo(
        () =>
            Array.from({length: count}, (_, i) => ({
                id: i,
                left: Math.random() * 100,
                delay: Math.random() * (once ? 0.6 : 2.2),
                duration: (once ? 1.6 : 2.8) + Math.random() * (once ? 0.8 : 2.6),
                size: 6 + Math.random() * 8,
                rotate: Math.random() * 360,
                color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
                round: Math.random() > 0.7,
            })),
        [count, once],
    );

    return (
        <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
            {pieces.map((p) => (
                <motion.span
                    key={p.id}
                    className="absolute top-0 block"
                    style={{
                        left: `${p.left}%`,
                        width: p.size,
                        height: p.size * (p.round ? 1 : 1.6),
                        backgroundColor: p.color,
                        borderRadius: p.round ? '9999px' : '2px',
                    }}
                    initial={{y: '-12vh', opacity: 0, rotate: 0}}
                    animate={{y: '112vh', opacity: [0, 1, 1, 0.9], rotate: p.rotate + 540}}
                    transition={{duration: p.duration, delay: p.delay, ease: 'linear', repeat: once ? 0 : Infinity}}
                />
            ))}
        </div>
    );
}

/**
 * What a leg's pill says, from the stored outcome: a draw is a draw, a walkover or a forfeit says
 * so, an abandoned game is nobody's. `tone` colours it: won green, lost red, the rest neutral.
 */
export function legLabel(leg: Pick<TournamentRoundupLeg, 'won' | 'outcome'>): {text: string; tone: 'won' | 'lost' | 'neutral'} {
    switch (leg.outcome) {
        case 'draw': return {text: 'Draw', tone: 'neutral'};
        case 'walkover_won': return {text: 'Won by walkover', tone: 'won'};
        case 'walkover_lost': return {text: 'Lost by walkover', tone: 'lost'};
        case 'forfeit_won': return {text: 'Won by forfeit', tone: 'won'};
        case 'forfeit_lost': return {text: 'Forfeited', tone: 'lost'};
        case 'awarded_won': return {text: 'Awarded', tone: 'won'};
        case 'awarded_lost': return {text: 'Awarded against', tone: 'lost'};
        case 'abandoned': return {text: 'Abandoned', tone: 'neutral'};
        case 'postponed': return {text: 'Postponed', tone: 'neutral'};
        case 'no_result': return {text: 'No result', tone: 'neutral'};
        case 'won': return {text: 'Won', tone: 'won'};
        case 'lost': return {text: 'Lost', tone: 'lost'};
        default: return leg.won ? {text: 'Won', tone: 'won'} : {text: 'Lost', tone: 'lost'};
    }
}

const TONE = {won: 'bg-[#16a34a] text-white', lost: 'bg-[#dc2626] text-white', neutral: 'bg-white/20 text-white'};
const TONE_ICON = {won: 'mdi:check-circle', lost: 'mdi:close-circle', neutral: 'mdi:minus-circle'};

const sideInitials = (label: string) => label.replace(/&amp;|&/g, ' ').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('') || '?';

/**
 * A side's pictures: a team's logo, or each player's photo (a pair overlaps two); without any,
 * its initials in a badge.
 */
export function SideAvatars({label, photos, size = 56}: {label: string; photos?: string[] | null; size?: number}) {
    const list = (photos ?? []).filter(Boolean).slice(0, 2);
    if (!list.length) {
        return (
            <span className="grid shrink-0 place-items-center rounded-full bg-white/15 font-bold text-white ring-2 ring-white/30"
                style={{width: size, height: size, fontSize: Math.round(size * 0.36)}}>
                {sideInitials(label)}
            </span>
        );
    }
    return (
        <span className="flex shrink-0 -space-x-3">
            {list.map((src) => (
                <img key={src} src={src} alt="" className="rounded-full object-cover ring-2 ring-white/40" style={{width: size, height: size}} />
            ))}
        </span>
    );
}

function LegSlide({leg, index, total}: {leg: TournamentRoundupLeg; index: number; total: number}) {
    const label = legLabel(leg);
    return (
        <div className="flex w-full flex-col items-center px-6 text-center">
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#cddbff]">
                Match {index + 1} of {total}
            </p>

            <h3 className="mt-3 font-bebas text-4xl font-bold tracking-wide text-white sm:text-5xl">{leg.round}</h3>

            <div className="mt-3"><SideAvatars label={leg.opponent} photos={leg.opponent_photos} size={48} /></div>
            <p className="mt-2 max-w-md break-words text-base text-white/70 sm:text-lg">
                vs <span className="font-semibold text-white">{leg.opponent}</span>
            </p>

            <p className="mt-6 font-mono text-4xl font-bold tabular-nums text-white sm:text-6xl">{leg.score || '—'}</p>

            <span
                className={'mt-5 inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wider ' + TONE[label.tone]}
                data-testid="leg-label"
            >
                <Icon icon={TONE_ICON[label.tone]} className="text-sm" />
                {label.text}
            </span>
        </div>
    );
}

/**
 * The champion announced: the trophy springs in, the pictures, "Champion", the name, a line under
 * it, and whatever the caller puts beneath (a button). With reduced motion it is simply there.
 */
export function ChampionReveal({winner, subtitle, reduced = false, size = 'lg', children}: {
    winner: {label: string; photos?: string[] | null};
    subtitle?: string | null;
    reduced?: boolean;
    /** The live view's full-screen size, or the completion screen's. */
    size?: 'lg' | 'md';
    children?: ReactNode;
}) {
    const still = reduced ? {initial: false as const} : {};
    const lg = size === 'lg';
    return (
        <motion.div
            className="flex flex-col items-center px-6 text-center"
            {...(reduced ? {initial: false} : {initial: {opacity: 0, scale: 0.9}, animate: {opacity: 1, scale: 1}, transition: {duration: 0.4}})}
        >
            <motion.div {...(reduced ? still : {initial: {scale: 0, rotate: -30}, animate: {scale: 1, rotate: 0}, transition: {type: 'spring', stiffness: 200, damping: 12, delay: 0.1}})}>
                <Icon icon="mdi:trophy" className={(lg ? 'text-7xl sm:text-8xl' : 'text-6xl') + ' text-[#f5c518] drop-shadow-lg'} />
                <div className="mt-3 flex justify-center"><SideAvatars label={winner.label} photos={winner.photos} size={lg ? 72 : 60} /></div>
            </motion.div>

            <motion.p className="mt-4 text-xs font-bold uppercase tracking-[0.4em] text-[#f5c518]"
                      {...(reduced ? still : {initial: {opacity: 0, y: 10}, animate: {opacity: 1, y: 0}, transition: {delay: 0.35}})}>
                Champion
            </motion.p>

            <motion.h2 className={'mt-2 max-w-xl break-words font-bebas font-bold tracking-wide text-white ' + (lg ? 'text-5xl sm:text-7xl' : 'text-4xl sm:text-5xl')}
                       {...(reduced ? still : {initial: {opacity: 0, y: 24, scale: 0.92}, animate: {opacity: 1, y: 0, scale: 1}, transition: {type: 'spring', stiffness: 140, damping: 14, delay: 0.5}})}>
                {winner.label}
            </motion.h2>

            {subtitle && (
                <motion.p className="mt-3 text-sm text-white/70 sm:text-base" {...(reduced ? still : {initial: {opacity: 0}, animate: {opacity: 1}, transition: {delay: 0.9}})}>
                    {subtitle}
                </motion.p>
            )}

            {children}
        </motion.div>
    );
}

/**
 * The route as a compact list, oldest first: the round, the opponent, the score and the pill —
 * the completion screen's and the public results page's reading of the road. Each row arrives a
 * beat after the last unless motion is reduced.
 */
export function RouteList({route, reduced = false, delay = 0, stagger = 0.08}: {route: TournamentRoundupLeg[]; reduced?: boolean; delay?: number; stagger?: number}) {
    return (
        <ol className="flex w-full flex-col gap-2" aria-label="Road to victory">
            {route.map((leg, i) => {
                const label = legLabel(leg);
                return (
                    <motion.li key={leg.game_uid} className="flex items-center gap-3 rounded-xl bg-white/[0.06] px-3 py-2 ring-1 ring-white/10"
                               {...(reduced ? {initial: false} : {initial: {opacity: 0, x: -16}, animate: {opacity: 1, x: 0}, transition: {delay: delay + i * stagger, duration: 0.25}})}>
                        <span className={'grid h-7 w-7 shrink-0 place-items-center rounded-full text-sm ' + TONE[label.tone]} title={label.text}>
                            <Icon icon={TONE_ICON[label.tone]} />
                        </span>
                        <span className="min-w-0 flex-1">
                            <span className="block text-[11px] font-bold uppercase tracking-wider text-[#cddbff]">{leg.round}</span>
                            <span className="block truncate text-sm text-white">vs <span className="font-semibold">{leg.opponent}</span></span>
                        </span>
                        <span className="shrink-0 font-mono text-sm font-bold tabular-nums text-white">{leg.score || label.text}</span>
                    </motion.li>
                );
            })}
        </ol>
    );
}

/** The route as the live view's carousel: one match at a time, swipe or arrows, dots beneath. */
export function RouteCarousel({route, slide, onGo}: {route: TournamentRoundupLeg[]; slide: number; onGo: (next: number) => void}) {
    return (
        <>
            {/* Swipe on touch, arrows/dots on a pointer. Keyed on the game so React remounts and
                replays the slide-in for each match. */}
            <div className="w-full overflow-hidden">
                <motion.div
                    key={route[slide].game_uid}
                    drag="x"
                    dragConstraints={{left: 0, right: 0}}
                    dragElastic={0.18}
                    onDragEnd={(_, info) => {
                        if (info.offset.x < -60) onGo(slide + 1);
                        if (info.offset.x > 60) onGo(slide - 1);
                    }}
                    initial={{opacity: 0, x: 40}}
                    animate={{opacity: 1, x: 0}}
                    transition={{duration: 0.28}}
                    className="cursor-grab active:cursor-grabbing"
                >
                    <LegSlide leg={route[slide]} index={slide} total={route.length} />
                </motion.div>
            </div>

            <div className="mt-8 flex items-center gap-4">
                <button
                    type="button"
                    onClick={() => onGo(slide - 1)}
                    disabled={slide === 0}
                    aria-label="Previous match"
                    className="rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20 disabled:opacity-30"
                >
                    <Icon icon="mdi:chevron-left" className="text-2xl" />
                </button>

                <div className="flex items-center gap-2">
                    {route.map((leg, i) => (
                        <button
                            key={leg.game_uid}
                            type="button"
                            onClick={() => onGo(i)}
                            aria-label={`Match ${i + 1}`}
                            className={
                                'h-2 rounded-full transition-all ' +
                                (i === slide ? 'w-6 bg-[#f5c518]' : 'w-2 bg-white/30 hover:bg-white/50')
                            }
                        />
                    ))}
                </div>

                <button
                    type="button"
                    onClick={() => onGo(slide + 1)}
                    disabled={slide === route.length - 1}
                    aria-label="Next match"
                    className="rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20 disabled:opacity-30"
                >
                    <Icon icon="mdi:chevron-right" className="text-2xl" />
                </button>
            </div>
        </>
    );
}

export default function RoadToVictory({
    tournamentUid,
    enabled,
    tvMode = false,
}: {
    tournamentUid: string;
    enabled: boolean;
    /** Venue screen (?tv or full screen): always plays, and loops rather than waiting to be closed. */
    tvMode?: boolean;
}) {
    const {data} = useQuery({
        queryKey: ['tournament-roundup', tournamentUid],
        queryFn: () => services.tournaments.roundup(tournamentUid),
        enabled: enabled && !!tournamentUid,
        staleTime: 60_000,
    });

    const r = data?.roundup;
    const route = r?.route;

    const [open, setOpen] = useState(false);
    const [phase, setPhase] = useState<'reveal' | 'route'>('reveal');
    const [slide, setSlide] = useState(0);
    // Bumped on manual navigation to restart the auto-advance timer from that slide.
    const [tick, setTick] = useState(0);
    const reduced = useMemo(prefersReducedMotion, []);

    const start = useCallback(() => {
        setPhase('reveal');
        setSlide(0);
        setTick((t) => t + 1);
        setOpen(true);
    }, []);

    // Auto-open: always on a venue screen, otherwise only the first time this browser sees the
    // finished event — spectators checking a score later shouldn't have to dismiss it every visit.
    useEffect(() => {
        if (!route?.length) return;
        if (tvMode) {
            start();
            return;
        }
        let seen = false;
        try {
            seen = localStorage.getItem(seenKey(tournamentUid)) === '1';
        } catch {
            seen = true; // private mode — don't nag
        }
        if (!seen) {
            try {
                localStorage.setItem(seenKey(tournamentUid), '1');
            } catch {
                /* private mode */
            }
            start();
        }
    }, [route?.length, tvMode, tournamentUid, start]);

    // Reveal holds, then the route begins.
    useEffect(() => {
        if (!open || phase !== 'reveal') return;
        const t = setTimeout(() => setPhase('route'), reduced ? 1200 : REVEAL_MS);
        return () => clearTimeout(t);
    }, [open, phase, reduced, tick]);

    // Carousel auto-advance. On a venue screen it loops back to the reveal; elsewhere it rests on
    // the final match so the result stays on screen.
    useEffect(() => {
        if (!open || phase !== 'route' || !route?.length) return;
        const t = setTimeout(() => {
            if (slide < route.length - 1) {
                setSlide((s) => s + 1);
            } else if (tvMode) {
                start();
            }
        }, SLIDE_MS);
        return () => clearTimeout(t);
    }, [open, phase, slide, route?.length, tvMode, start, tick]);

    const close = useCallback(() => setOpen(false), []);

    const go = useCallback(
        (next: number) => {
            if (!route?.length) return;
            setSlide(Math.max(0, Math.min(route.length - 1, next)));
            setTick((t) => t + 1);
        },
        [route?.length],
    );

    // Escape closes; arrows step through the route.
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') close();
            if (phase === 'route' && e.key === 'ArrowRight') go(slide + 1);
            if (phase === 'route' && e.key === 'ArrowLeft') go(slide - 1);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, phase, slide, go, close]);

    if (!r || !route?.length) {
        return null;
    }

    const {winner, stats} = r;
    const subtitle = [
        winner.decided_by === 'final' ? 'Won the final' : 'Topped the standings',
        stats.matches_played ? `${stats.matches_played} matches` : null,
        stats.participants ? `${stats.participants} entrants` : null,
    ]
        .filter(Boolean)
        .join(' · ');

    return (
        <>
            {/* Persistent entry point once the celebration has been dismissed. */}
            {!open && (
                <div className="mt-4 flex justify-center">
                    <button
                        type="button"
                        onClick={start}
                        className="inline-flex items-center gap-2 rounded-full bg-white/10 px-5 py-2.5 text-sm font-semibold text-white ring-1 ring-white/20 backdrop-blur transition hover:bg-white/20"
                    >
                        <Icon icon="mdi:trophy" className="text-lg text-[#f5c518]" />
                        <span className="truncate">{winner.label}</span>
                        <span className="text-white/60">— road to victory</span>
                    </button>
                </div>
            )}

            {/* Rendered conditionally rather than through AnimatePresence. An exit-animated unmount
                left this full-screen layer in the DOM at opacity 0 with pointer-events auto, which
                silently swallowed every click on the live view once the celebration was dismissed. */}
            {open && (
                    <motion.div
                        role="dialog"
                        aria-modal="true"
                        aria-label="Road to victory"
                        className="fixed inset-0 z-[100] flex items-center justify-center bg-[#04102e]/95 backdrop-blur-sm"
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                        transition={{duration: 0.35}}
                    >
                        {phase === 'reveal' && !reduced && <Confetti />}

                        <button
                            type="button"
                            onClick={close}
                            aria-label="Close"
                            className="absolute right-4 top-4 z-10 rounded-full bg-white/10 p-2 text-white/70 transition hover:bg-white/20 hover:text-white"
                        >
                            <Icon icon="mdi:close" className="text-xl" />
                        </button>

                        {/* Phases swap by keyed remount rather than AnimatePresence: an exit-driven
                            swap leaves the outgoing phase on screen forever if its exit never
                            settles, which strands the whole celebration on the reveal. */}
                        <div className="relative flex w-full max-w-2xl flex-col items-center">
                            <>
                                {phase === 'reveal' ? (
                                    <ChampionReveal key="reveal" winner={winner} subtitle={subtitle}>
                                        <motion.button
                                            type="button"
                                            onClick={() => setPhase('route')}
                                            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#f5c518] px-6 py-3 text-sm font-bold text-[#04102e] transition hover:brightness-110"
                                            initial={{opacity: 0}}
                                            animate={{opacity: 1}}
                                            transition={{delay: 1.2}}
                                        >
                                            See the road to victory
                                            <Icon icon="mdi:arrow-right" className="text-base" />
                                        </motion.button>
                                    </ChampionReveal>
                                ) : (
                                    <motion.div
                                        key="route"
                                        className="flex w-full flex-col items-center"
                                        initial={{opacity: 0}}
                                        animate={{opacity: 1}}
                                        transition={{duration: 0.3}}
                                    >
                                        <p className="mb-6 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.3em] text-[#cddbff]">
                                            <Icon icon="mdi:trophy" className="text-sm text-[#f5c518]" />
                                            <span className="max-w-[60vw] truncate">{winner.label}</span>
                                        </p>

                                        <RouteCarousel route={route} slide={slide} onGo={go} />

                                        {!tvMode && slide === route.length - 1 && (
                                            <button
                                                type="button"
                                                onClick={close}
                                                className="mt-8 rounded-full bg-white/10 px-6 py-2.5 text-sm font-semibold text-white ring-1 ring-white/20 transition hover:bg-white/20"
                                            >
                                                Done
                                            </button>
                                        )}
                                    </motion.div>
                                )}
                            </>
                        </div>
                    </motion.div>
            )}
        </>
    );
}
